import React from "react";
import { Link } from "react-router-dom";

const AboutPage = () => {
  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <Link to="/" style={styles.logo}>striply</Link>
        <nav style={styles.nav}>
          <Link to="/editor" style={styles.navLink}>Editor</Link>
          <Link to="/tutorial" style={styles.navLink}>Tutorial</Link>
        </nav>
      </header>

      <main style={styles.main}>
        <h1 style={styles.title}>About Striply</h1>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Our Mission</h2>
          <p style={styles.text}>
            Striply exists to make comic creation accessible to everyone. We believe
            that the technical challenges of page layout shouldn't stand between
            creators and their stories.
          </p>
          <p style={styles.text}>
            Professional comic software can be overwhelming and expensive. Striply
            offers a focused, intuitive alternative for designing page layouts quickly
            and exporting them for print or digital use.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>What Striply Does</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Panel Layout Design:</strong> Create custom comic page layouts
              with our drag-and-drop editor. Resize panels by dragging edges or corners.
            </li>
            <li style={styles.listItem}>
              <strong>Speech Bubbles:</strong> Add dialogue with customizable speech
              bubbles. Choose shapes, tail positions, and text alignment.
            </li>
            <li style={styles.listItem}>
              <strong>Background Images:</strong> Upload artwork to fill your panels
              and see how your compositions look in context.
            </li>
            <li style={styles.listItem}>
              <strong>Print-Ready Export:</strong> Download high-resolution PDFs sized
              for standard letter pages (8.5" x 11").
            </li>
            <li style={styles.listItem}>
              <strong>Layout Templates:</strong> Start with pre-built grids and
              customize from there.
            </li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Who It's For</h2>
          <p style={styles.text}>
            Striply is designed for:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Hobbyist creators</strong> who want to make comics without
              learning complex software
            </li>
            <li style={styles.listItem}>
              <strong>Aspiring comic artists</strong> who need a quick way to
              prototype page layouts
            </li>
            <li style={styles.listItem}>
              <strong>Educators and students</strong> learning visual storytelling
              and comic design
            </li>
            <li style={styles.listItem}>
              <strong>Writers</strong> who want to visualize their scripts before
              working with an artist
            </li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Our Approach</h2>
          <p style={styles.text}>
            We focus on simplicity and speed. Striply won't replace full illustration
            software - and it's not trying to. Instead, it excels at one thing:
            making panel layout design fast and frustration-free.
          </p>
          <p style={styles.text}>
            Portrait orientation is our default because that's how most comics are
            printed. Standard letter size (8.5" x 11") works with home printers and
            professional print services alike.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Get Started</h2>
          <p style={styles.text}>
            Ready to create your first page?
          </p>
          <Link to="/editor" style={styles.ctaButton}>
            Open the Editor
          </Link>
        </section>
      </main>

      <footer style={styles.footer}>
        <nav style={styles.footerNav}>
          <Link to="/" style={styles.footerLink}>Home</Link>
          <Link to="/editor" style={styles.footerLink}>Editor</Link>
          <Link to="/tutorial" style={styles.footerLink}>Tutorial</Link>
          <Link to="/privacy" style={styles.footerLink}>Privacy</Link>
          <Link to="/terms" style={styles.footerLink}>Terms</Link>
        </nav>
      </footer>
    </div>
  );
};

const styles = {
  container: {
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#1a1a1a",
    lineHeight: 1.7,
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "#f5f0e8",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 40px",
    borderBottom: "2px solid #1a1a1a",
  },
  logo: {
    fontSize: 28,
    fontWeight: 700,
    color: "#1a1a1a",
    textDecoration: "none",
    fontFamily: "'Georgia', serif",
  },
  nav: {
    display: "flex",
    gap: 20,
  },
  navLink: {
    color: "#555",
    textDecoration: "none",
    fontSize: 15,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  main: {
    flex: 1,
    maxWidth: 720,
    margin: "0 auto",
    padding: "48px 24px",
  },
  title: {
    fontSize: 42,
    fontWeight: 700,
    marginBottom: 40,
    color: "#1a1a1a",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  section: {
    marginBottom: 40,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 16,
    color: "#1a1a1a",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  text: {
    fontSize: 16,
    marginBottom: 16,
    color: "#444",
  },
  list: {
    paddingLeft: 24,
    marginBottom: 16,
  },
  listItem: {
    fontSize: 16,
    marginBottom: 12,
    color: "#444",
  },
  ctaButton: {
    display: "inline-block",
    padding: "14px 32px",
    background: "#1a1a1a",
    color: "#f5f0e8",
    fontSize: 16,
    fontWeight: 700,
    borderRadius: 0,
    textDecoration: "none",
    marginTop: 8,
    textTransform: "uppercase",
    letterSpacing: 1,
    border: "2px solid #1a1a1a",
  },
  footer: {
    padding: "24px 40px",
    borderTop: "2px solid #1a1a1a",
    background: "#f5f0e8",
  },
  footerNav: {
    display: "flex",
    justifyContent: "center",
    gap: 32,
  },
  footerLink: {
    color: "#666",
    textDecoration: "none",
    fontSize: 14,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
};

export default AboutPage;
